/**
 * The horizontal scroll chevrons that overlay an AG Grid header.
 *
 * DataGrid, BillTable and NestedBillTable each carry their own copy of this
 * logic against the same `.ag-scroll-chevron` styles in index.css. This is that
 * behaviour as a hook plus its markup, so a grid that needs the affordance can
 * have it without a fourth hand-roll. The three existing copies are untouched;
 * they can adopt this separately.
 *
 * The chevrons key off the center viewport overflowing: a grid whose columns
 * are all flex-sized never overflows, and so never shows them.
 */

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';

/** Give up looking for AG Grid's viewport after ~2s of frames. */
const MAX_ATTACH_FRAMES = 120;

export interface GridScrollChevronsState {
  /** Put this on the element wrapping the grid. It must be position:relative. */
  wrapperRef: React.RefObject<HTMLDivElement>;
  canScrollLeft: boolean;
  canScrollRight: boolean;
  startScrolling: (direction: 'left' | 'right') => void;
  stopScrolling: () => void;
}

export function useGridScrollChevrons(): GridScrollChevronsState {
  const wrapperRef = useRef<HTMLDivElement>(null);
  const scrollAnimRef = useRef<number | null>(null);
  const isScrollingRef = useRef(false);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  const updateScrollState = useCallback(() => {
    if (!wrapperRef.current || isScrollingRef.current) return;
    const viewport = wrapperRef.current.querySelector('.ag-center-cols-viewport') as HTMLElement | null;
    if (!viewport) return;
    const { scrollLeft, scrollWidth, clientWidth } = viewport;
    setCanScrollLeft(scrollLeft > 2);
    setCanScrollRight(scrollLeft + clientWidth < scrollWidth - 2);

    const pinnedContainer = wrapperRef.current.querySelector('.ag-pinned-left-header') as HTMLElement | null;
    if (pinnedContainer) {
      wrapperRef.current.style.setProperty('--ag-chevron-left-offset', `${pinnedContainer.offsetWidth}px`);
    }
  }, []);

  useEffect(() => {
    let frame = 0;
    let frames = 0;
    let viewport: HTMLElement | null = null;
    let scrollTimeout: ReturnType<typeof setTimeout>;

    const observer = new ResizeObserver(updateScrollState);
    const handleScroll = () => {
      clearTimeout(scrollTimeout);
      scrollTimeout = setTimeout(updateScrollState, 100);
    };

    // AG Grid builds its viewport after mount, so wait for it rather than
    // depending on row data — which would rebind on every fetch and still miss
    // a column-width change.
    const attach = () => {
      viewport = wrapperRef.current?.querySelector('.ag-center-cols-viewport') as HTMLElement | null;
      if (!viewport) {
        if (frames++ > MAX_ATTACH_FRAMES) return;
        frame = requestAnimationFrame(attach);
        return;
      }
      viewport.addEventListener('scroll', handleScroll);
      observer.observe(viewport);
      // The container is as wide as the columns, so observing it catches a
      // width change that leaves the viewport itself the same size.
      const container = wrapperRef.current?.querySelector('.ag-center-cols-container') as HTMLElement | null;
      if (container) observer.observe(container);
      updateScrollState();
    };
    attach();

    return () => {
      cancelAnimationFrame(frame);
      clearTimeout(scrollTimeout);
      viewport?.removeEventListener('scroll', handleScroll);
      observer.disconnect();
    };
  }, [updateScrollState]);

  const startScrolling = useCallback((direction: 'left' | 'right') => {
    isScrollingRef.current = true;
    const pixelsPerFrame = direction === 'left' ? -8 : 8;
    const animate = () => {
      if (!wrapperRef.current) return;
      const viewport = wrapperRef.current.querySelector('.ag-center-cols-viewport') as HTMLElement | null;
      if (!viewport) return;
      viewport.scrollLeft += pixelsPerFrame;
      scrollAnimRef.current = requestAnimationFrame(animate);
    };
    scrollAnimRef.current = requestAnimationFrame(animate);
  }, []);

  const stopScrolling = useCallback(() => {
    if (scrollAnimRef.current) {
      cancelAnimationFrame(scrollAnimRef.current);
      scrollAnimRef.current = null;
    }
    isScrollingRef.current = false;
    updateScrollState();
  }, [updateScrollState]);

  useEffect(() => () => {
    if (scrollAnimRef.current) cancelAnimationFrame(scrollAnimRef.current);
  }, []);

  return { wrapperRef, canScrollLeft, canScrollRight, startScrolling, stopScrolling };
}

export interface GridScrollChevronsProps
  extends Omit<GridScrollChevronsState, 'wrapperRef'> {
  /** Match the grid's headerHeight so the chevrons cover the header exactly. */
  headerHeight?: number;
}

const GridScrollChevrons: React.FC<GridScrollChevronsProps> = ({
  canScrollLeft,
  canScrollRight,
  startScrolling,
  stopScrolling,
  headerHeight,
}) => {
  const style = headerHeight ? { height: headerHeight } : undefined;
  return (
    <>
      <button
        className={`ag-scroll-chevron ag-scroll-chevron-left ${canScrollLeft ? 'ag-scroll-chevron-visible' : 'ag-scroll-chevron-hidden'}`}
        style={style}
        onMouseDown={() => startScrolling('left')}
        onMouseUp={stopScrolling}
        onMouseLeave={stopScrolling}
        onTouchStart={() => startScrolling('left')}
        onTouchEnd={stopScrolling}
        aria-label="Scroll table left"
        tabIndex={canScrollLeft ? 0 : -1}
      >
        <ChevronLeft className="w-5 h-5" />
      </button>

      <button
        className={`ag-scroll-chevron ag-scroll-chevron-right ${canScrollRight ? 'ag-scroll-chevron-visible' : 'ag-scroll-chevron-hidden'}`}
        style={style}
        onMouseDown={() => startScrolling('right')}
        onMouseUp={stopScrolling}
        onMouseLeave={stopScrolling}
        onTouchStart={() => startScrolling('right')}
        onTouchEnd={stopScrolling}
        aria-label="Scroll table right"
        tabIndex={canScrollRight ? 0 : -1}
      >
        <ChevronRight className="w-5 h-5" />
      </button>
    </>
  );
};

export default GridScrollChevrons;
